import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useDevices, useDeviceFilters, useActivateDevice, useBlockDevice } from '../hooks';
import { useDeviceColumns, DevicesDataTable } from '../components';

export function DevicesPage() {
  const { t } = useTranslation();
  const { filters, updateFilter, updateFilters, resetFilters, handleSort } = useDeviceFilters();

  const { data, isLoading, isError, error } = useDevices(filters);
  const { mutate: activateDevice, isPending: isActivatePending } = useActivateDevice();
  const { mutate: blockDevice, isPending: isBlockPending } = useBlockDevice();

  const handleToggle = (id: number, status: 'ACTIVE' | 'BLOCKED') => {
    if (status === 'ACTIVE') {
      activateDevice(id);
    } else {
      blockDevice(id);
    }
  };

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = useState<HTMLDivElement | null>(null);
  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const columns = useDeviceColumns({
    orderBy: filters.orderBy || '',
    order: filters.order || '',
    onSort: handleSort,
    filters,
    onToggle: handleToggle,
    isTogglePending: isActivatePending || isBlockPending,
  });

  if (isError) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Error loading devices'}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b-1">
        <div className="flex items-center gap-4">
          <Link to="/app/users">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back')}
            </Button>
          </Link>
          <h1 className="text-xl font-semibold">{t('devices')}</h1>
        </div>
        <div ref={setTableControlsEl} className="flex items-center gap-2" />
      </div>
      <div className="px-4 pt-4">
        <DevicesDataTable
          tableZoom={tableZoom}
          controlsContainer={tableControlsEl}
          data={data?.results || []}
          columns={columns}
          isLoading={isLoading}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? data?.total ?? 0}
          onPageChange={(val) => updateFilter('page', val)}
          onLimitChange={(val) => updateFilters({ limit: val, page: 0 })}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
